import { ItemsPerPage, useItemsPerPage } from '@billy/design-system'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo } from 'react'

import { RecurringInvoicesSortProperty } from '@features/recurringInvoices/enums/recurringInvoicesSortProperty'
import { RecurringInvoiceStatus } from '@features/recurringInvoices/enums/recurringInvoiceStatus'

import { SortDirection } from '../../../../../enums/sortDirection'
import { useQueryParams } from '../../../../../hooks/useQueryParams'

export interface RecurringInvoicesListFilters {
  page?: number
  pageSize?: ItemsPerPage
  sortDirection?: SortDirection
  sortProperty?: RecurringInvoicesSortProperty
  status?: RecurringInvoiceStatus
}

export const defaultQuery: RecurringInvoicesListFilters = {
  page: 1,
  pageSize: undefined,
  sortDirection: SortDirection.Desc,
  sortProperty: RecurringInvoicesSortProperty.Date,
  status: RecurringInvoiceStatus.All,
}

type ContextState = [RecurringInvoicesListFilters, (filters: Partial<RecurringInvoicesListFilters>) => void]

export interface RecurringInvoicesListFiltersProviderProps {
  children?: ReactNode
  contextFilters?: Partial<RecurringInvoicesListFilters>
}

const RecurringInvoicesListFiltersContext = createContext<ContextState | undefined>(undefined)

export const RecurringInvoicesListFiltersProvider = ({
  children,
  contextFilters,
}: RecurringInvoicesListFiltersProviderProps): ReactElement => {
  const [pageSize] = useItemsPerPage()
  const [queryParams, setQueryParams] = useQueryParams<RecurringInvoicesListFilters>({
    defaultQuery: {
      ...defaultQuery,
      ...contextFilters,
      pageSize,
    },
  })

  const filters = useMemo(
    () => ({
      ...queryParams,
      // Sometimes `page` comes as a string. It needs to be always `number` type.
      page: Number(queryParams.page),
      // Sometimes `pageSize` comes as string. It needs to be always `number` type.
      pageSize: Number(queryParams.pageSize) as ItemsPerPage,
    }),
    [queryParams],
  )

  const setFilters = useCallback(
    (newFilters: Partial<RecurringInvoicesListFilters>) => {
      setQueryParams(newFilters)
    },
    [setQueryParams],
  )

  const value = useMemo((): ContextState => [filters, setFilters], [filters, setFilters])

  return (
    <RecurringInvoicesListFiltersContext.Provider value={value}>
      {children}
    </RecurringInvoicesListFiltersContext.Provider>
  )
}

export const useRecurringInvoicesListFilters = () => {
  const context = useContext(RecurringInvoicesListFiltersContext)

  if (!context) {
    throw new Error('RecurringInvoicesListFiltersContext is missing in the module!')
  }

  return context
}
